import { lazy, Suspense } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Select } from '../components/ui/Select'

const OverviewTab = lazy(() => import('../components/platform/OverviewTab'))
const TenantsTab = lazy(() => import('../components/platform/TenantsTab'))
const AuditTab = lazy(() => import('../components/platform/AuditTab'))
const FeaturesTab = lazy(() => import('../components/platform/FeaturesTab'))
const AlertsTab = lazy(() => import('../components/platform/AlertsTab'))
const D1Tab = lazy(() => import('../components/platform/D1Tab'))
const KVTab = lazy(() => import('../components/platform/KVTab'))
const R2Tab = lazy(() => import('../components/platform/R2Tab'))
const SecurityTab = lazy(() => import('../components/platform/SecurityTab'))
const AnnouncementsTab = lazy(() => import('../components/platform/AnnouncementsTab'))
const ProductsTab = lazy(() => import('../components/platform/ProductsTab'))
const AIUsageTab = lazy(() => import('../components/platform/AIUsageTab'))
const DeviceModuleListTab = lazy(() => import('./DeviceModuleList'))
const VultrPlatformTab = lazy(() => import('../components/platform/VultrPlatformTab'))

const PLATFORM_NAV = [
  { group: 'BUSINESS', items: [
    { id: 'overview', label: 'Overview' },
    { id: 'tenants', label: 'Tenants' },
    { id: 'ai-usage', label: 'AI Usage' },
  ]},
  { group: 'CATALOG', items: [
    { id: 'products', label: 'Products' },
    { id: 'device-modules', label: 'Device Modules' },
  ]},
  { group: 'OPERATIONS', items: [
    { id: 'features', label: 'Features' },
    { id: 'announcements', label: 'Announcements' },
    { id: 'audit', label: 'Audit' },
    { id: 'security', label: 'Security' },
    { id: 'alerts', label: 'Alerts' },
  ]},
  { group: 'INFRASTRUCTURE', items: [
    { id: 'vultr', label: 'Vultr Cloud' },
    { id: 'd1', label: 'Database' },
    { id: 'kv', label: 'KV Store' },
    { id: 'r2', label: 'Storage' },
  ]},
] as const

type SectionId = typeof PLATFORM_NAV[number]['items'][number]['id']

const PATH_TO_SECTION: Record<string, SectionId> = {}
for (const g of PLATFORM_NAV) {
  for (const item of g.items) {
    PATH_TO_SECTION[item.id] = item.id
  }
}

export default function PlatformAdmin() {
  const navigate = useNavigate()
  const location = useLocation()

  const subPath = location.pathname.replace(/^\/platform\/?/, '').split('/')[0]
  const section: SectionId = (subPath && PATH_TO_SECTION[subPath]) || 'overview'

  function handleNavClick(id: SectionId) {
    navigate(`/platform/${id}`, { replace: id === 'overview' })
  }

  function renderContent() {
    const fallback = <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading...</div>

    switch (section) {
      case 'overview': return <Suspense fallback={fallback}><OverviewTab /></Suspense>
      case 'tenants': return <Suspense fallback={fallback}><TenantsTab /></Suspense>
      case 'ai-usage': return <Suspense fallback={fallback}><AIUsageTab /></Suspense>
      case 'products': return <Suspense fallback={fallback}><ProductsTab /></Suspense>
      case 'device-modules': return <Suspense fallback={fallback}><DeviceModuleListTab /></Suspense>
      case 'audit': return <Suspense fallback={fallback}><AuditTab /></Suspense>
      case 'features': return <Suspense fallback={fallback}><FeaturesTab /></Suspense>
      case 'alerts': return <Suspense fallback={fallback}><AlertsTab /></Suspense>
      case 'd1': return <Suspense fallback={fallback}><D1Tab /></Suspense>
      case 'kv': return <Suspense fallback={fallback}><KVTab /></Suspense>
      case 'r2': return <Suspense fallback={fallback}><R2Tab /></Suspense>
      case 'security': return <Suspense fallback={fallback}><SecurityTab /></Suspense>
      case 'announcements': return <Suspense fallback={fallback}><AnnouncementsTab /></Suspense>
      case 'vultr': return <Suspense fallback={fallback}><VultrPlatformTab /></Suspense>
      default: return null
    }
  }

  return (
    <div className="page-enter">
      <style>{`
        .platform-nav {
          width: 170px;
          flex-shrink: 0;
          border-right: 1px solid var(--border);
          padding-right: 12px;
          position: sticky;
          top: 0;
          align-self: flex-start;
          max-height: calc(100vh - 52px);
          overflow-y: auto;
        }
        .platform-nav-group {
          font-size: 9px;
          font-weight: 800;
          color: var(--text-3);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 6px 10px;
          margin-top: 12px;
        }
        .platform-nav-group:first-child { margin-top: 0; }
        .platform-nav-item {
          display: block;
          width: 100%;
          padding: 6px 10px;
          border-radius: 6px;
          border: none;
          background: transparent;
          color: var(--text-3);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          text-align: left;
          transition: all 0.1s;
          font-family: var(--font);
        }
        .platform-nav-item:hover {
          background: rgba(255,255,255,.03);
          color: var(--text-2);
        }
        .platform-nav-item.active {
          background: var(--accent-m);
          color: var(--accent);
          font-weight: 600;
        }
        .platform-mobile-nav { display: none; margin-bottom: 16px; }
        @media (max-width: 768px) {
          .platform-mobile-nav { display: block; }
          .platform-nav { display: none; }
        }
      `}</style>

      {/* Two-column layout: grouped nav + content */}
      <div style={{ display: 'flex', gap: 0 }}>
        {/* Mobile nav dropdown */}
        <div className="platform-mobile-nav">
          <Select
            options={PLATFORM_NAV.flatMap(g => g.items.map(i => ({ value: i.id, label: i.label })))}
            value={section}
            onChange={(e) => handleNavClick(e.target.value as SectionId)}
          />
        </div>

        {/* Sidebar nav */}
        <div className="platform-nav">
          {PLATFORM_NAV.map(group => (
            <div key={group.group}>
              <div className="platform-nav-group">{group.group}</div>
              {group.items.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`platform-nav-item ${section === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Content area */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 16 }}>
          <div className="page-enter" key={section}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  )
}
